/**
 * Copyright: ThoughtSpot Inc. 2020
 * Author: Thawsitt Naing (thawsitt.naing@thoughtspot.com)
 *
 * @fileoverview Muted Alert for Radiant
 */
import cx from 'classnames';
import React from 'react';
import { Button } from '../../button';
import { InlineSvg } from '../../inline-svg';
import { Colors, Typography } from '../../typography';
import { GLOBAL_MUTED_ALERT_DEFAULT_ILLUSTRATION, LOCAL_MUTED_ALERT_DEFAULT_ILLUSTRATION, } from '../alert-constants';
import { createActionLink, getMutedAlertSVGPath } from '../alert-utils';
import styles from './muted-alert.module.scss';
export const MutedAlert = ({ global = false, title, coloredTitle = '', subtitle, description, isVisible = true, size = 'regular', primaryAction, secondaryAction, link, errorCode, className = '', imageVisible = true, descriptionTooltip, maxLinesForDescription, ...restProps }) => {
    const localOrGlobal = global === true ? 'global' : 'local';
    const actionLink = link
        ? createActionLink(link, cx(styles.link, styles[size], styles[localOrGlobal]))
        : null;
    const textVariant = {
        global: {
            title: 'page-title',
            description: 'body-large',
            color: Colors.base,
        },
        local: {
            title: 'content-label-subhead',
            description: 'body-normal',
            color: Colors.grayLight,
        },
    };
    const buttonSizes = {
        small: 's',
        regular: 'm',
    };
    const buttonSize = buttonSizes[size];
    const primaryActionButton = primaryAction ? (React.createElement(Button, { text: primaryAction.label, type: (primaryAction === null || primaryAction === void 0 ? void 0 : primaryAction.type) || 'primary', size: buttonSize, backgroundType: "light", icon: primaryAction.icon, className: cx(styles.primaryAction, primaryAction.className), onClick: primaryAction.onClick, isDisabled: primaryAction.isDisabled, tooltip: primaryAction.tooltip })) : null;
    const secondaryActionButton = secondaryAction ? (React.createElement(Button, { text: secondaryAction.label, type: "secondary", size: buttonSize, backgroundType: "light", icon: secondaryAction.icon, className: cx(styles.secondaryAction, secondaryAction.className), onClick: secondaryAction.onClick, isDisabled: secondaryAction.isDisabled, tooltip: secondaryAction.tooltip })) : null;
    const svgName = errorCode ||
        (global
            ? GLOBAL_MUTED_ALERT_DEFAULT_ILLUSTRATION
            : LOCAL_MUTED_ALERT_DEFAULT_ILLUSTRATION);
    const getIllustrationImage = () => {
        if (!imageVisible) {
            return undefined;
        }
        return (React.createElement(InlineSvg, { className: cx(styles.illustration, styles[size], styles[localOrGlobal]), path: getMutedAlertSVGPath(svgName) }));
    };
    const containerClass = cx(className, styles.container, styles[size], styles[localOrGlobal]);
    if (!isVisible) {
        return null;
    }
    return (React.createElement("div", { className: containerClass, ...restProps },
        getIllustrationImage(),
        React.createElement("div", { className: cx(styles.content, styles[size], styles[localOrGlobal]) },
            title && (React.createElement("div", { className: cx(styles.titleWrapper, styles[localOrGlobal]) },
                React.createElement(Typography, { variant: textVariant[localOrGlobal].title, color: textVariant[localOrGlobal].color, className: cx(styles.title, styles[localOrGlobal]) }, title),
                localOrGlobal === 'global' && coloredTitle && (React.createElement(Typography, { variant: textVariant[localOrGlobal].title, color: Colors.gray, className: cx(styles.title, styles[localOrGlobal], styles.coloredTitle) }, coloredTitle)))),
            global && subtitle && (React.createElement(Typography, { variant: "content-label", color: Colors.base, className: styles.subTitle }, subtitle)),
            description && (React.createElement(Typography, { variant: textVariant[localOrGlobal].description, color: textVariant[localOrGlobal].color, className: styles.description, tooltipConfig: {
                    dataTooltipContent: descriptionTooltip,
                    disableTooltip: !maxLinesForDescription,
                }, ellipsis: { rows: maxLinesForDescription } }, description)),
            React.createElement("div", { className: cx(styles.actionButtons, styles[buttonSize]) },
                secondaryActionButton,
                primaryActionButton),
            actionLink)));
};
//# sourceMappingURL=muted-alert.js.map